import { mutate as mutateCache } from 'swr'
import type { IdentityFieldPatchResponse } from '@digital-twins/shared-types'
import type {
  IdentityFieldSummary,
  IdentityWorkspaceMode,
} from '@/types/components/audiences/import-session'
import {
  getAudienceImportIdentitySourceOptionsKey,
  getAudienceImportIdentityValueMapKey,
} from '../../../lib/cache-keys'

/**
 * When the server response lags behind a full manual value map, merge an
 * optimistic satisfied field so the grid and navigation update immediately.
 */
export function buildOptimisticValueMapField(
  activeField: IdentityFieldSummary | null,
  response: IdentityFieldPatchResponse,
  options?: {
    sourceColumn?: string
    valueMap?: Record<string, string>
    fromConfirmSource?: boolean
  },
): IdentityFieldSummary | null {
  if (!activeField || !options?.valueMap) {
    return null
  }
  if (response.field.canonicalId !== activeField.canonicalId) {
    return null
  }
  const mappedValues = Object.values(options.valueMap).filter((value) =>
    value.trim(),
  )
  if (
    mappedValues.length === 0 ||
    mappedValues.length !== Object.keys(options.valueMap).length
  ) {
    return null
  }
  if (response.field.satisfied && response.field.nextAction !== 'map_values') {
    return null
  }

  return {
    ...activeField,
    currentSourceColumn:
      options.sourceColumn ?? activeField.currentSourceColumn,
    satisfied: true,
    status: 'resolved',
    state: 'ready_from_source',
    nextAction: 'done',
    requiresConfirmation: false,
    requiresValueMapping: false,
    valueMappingSummary: {
      mappedCount: mappedValues.length,
      unmappedCount: 0,
    },
  }
}

/**
 * Keeps SWR identity workspace caches aligned with a successful patch so
 * ValueMappingEditor initialValueMap updates without waiting for a refetch.
 */
export function syncIdentityWorkspaceCaches(args: {
  sessionId: string | null | undefined
  activeField: IdentityFieldSummary | null
  currentMode: IdentityWorkspaceMode | null
  debouncedSourceSearch: string
  pageSize: number
  options?: {
    sourceColumn?: string
    valueMap?: Record<string, string>
  }
}): void {
  const {
    sessionId,
    activeField,
    currentMode,
    debouncedSourceSearch,
    pageSize,
    options,
  } = args
  if (!sessionId || !activeField) {
    return
  }

  if (currentMode === 'confirm_source' && options?.sourceColumn) {
    const key = getAudienceImportIdentitySourceOptionsKey(
      sessionId,
      activeField.canonicalId,
      debouncedSourceSearch,
      0,
      pageSize,
    )
    if (!key) {
      return
    }
    void mutateCache(
      key,
      (
        current: {
          currentSourceColumn: string | null
        } | null | undefined,
      ) =>
        current
          ? {
              ...current,
              currentSourceColumn:
                options.sourceColumn ?? current.currentSourceColumn,
            }
          : current,
      { revalidate: false },
    )
    return
  }

  if (currentMode === 'map_values' && options?.valueMap) {
    const key = getAudienceImportIdentityValueMapKey(
      sessionId,
      activeField.canonicalId,
      options?.sourceColumn ?? null,
    )
    if (!key) {
      return
    }
    void mutateCache(
      key,
      (
        current: {
          sourceColumn: string | null
          currentValueMap: Record<string, string>
        } | null | undefined,
      ) =>
        current
          ? {
              ...current,
              sourceColumn: options.sourceColumn ?? current.sourceColumn,
              currentValueMap: options.valueMap ?? current.currentValueMap,
            }
          : current,
      { revalidate: false },
    )
  }
}

/** Refetch identity value-map workspace (e.g. after confirm_source while in map_values). */
export function revalidateIdentityValueMapWorkspace(
  sessionId: string | null | undefined,
  canonicalId: string | null | undefined,
): void {
  if (!sessionId || !canonicalId) {
    return
  }
  void mutateCache(
    (cacheKey) =>
      Array.isArray(cacheKey) &&
      cacheKey[0] === 'audience-import-identity-value-map' &&
      cacheKey[1] === sessionId &&
      cacheKey[2] === canonicalId,
    undefined,
    { revalidate: true },
  )
}
